import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UsersIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCourses } from '../contexts/CourseContext';
import { useApplications } from '../contexts/ApplicationContext';

const ProgramList: React.FC = () => {
  const { courses } = useCourses();
  const { getApplicationsByCourse } = useApplications();
  const [selectedStatus, setSelectedStatus] = useState<string>('전체');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const statusOptions = ['전체', '모집중', '진행중', '종료'];

  const filteredCourses = courses.filter(course => {
    const matchesStatus = selectedStatus === '전체' ||
      (selectedStatus === '모집중' && course.status === 'recruiting') ||
      (selectedStatus === '진행중' && course.status === 'ongoing') ||
      (selectedStatus === '종료' && course.status === 'completed');
    const matchesSearch = course.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          course.courseDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR');
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header with Image */}
      <div className="relative text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&h=600&fit=crop&q=80"
            alt="Global Education"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-8 relative z-10">
          <p className="text-gray-200 mb-3 text-sm font-medium tracking-wider">프로그램</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">프로그램 소개</h1>
          <div className="w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Mission Section - White Background */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-relaxed">
              언어와 문화를 융합한
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-green-600 mb-8 leading-relaxed">
              한국형 국제교육 모델의 중심
            </h2>
            <p className="text-xl text-gray-500 mb-6">
              The Center of Korean-Style Global Education Integrating Language and Culture
            </p>
            <div className="w-20 h-1 bg-green-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* About Section - Gray Background */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <p className="text-xl text-gray-700 leading-relaxed text-center">
              동두천 글로벌인재교육센터(DGTC)는 글로벌 시대에 필요한 <span className="font-bold text-gray-900">언어 능력과 문화 감수성을 기르기 위해</span> 영어 중심의 국제교육 프로그램을 운영하고 있습니다.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              유아부터 성인까지 전 연령을 대상으로 <span className="font-semibold text-green-600">언어·STEAM·AI·문화체험을 융합한 학습</span>을 통해 참가자가 직접 보고, 듣고, 느끼며 배우는 체험형 글로벌 교육을 실현합니다.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              또한, 외국인 학습자를 위한 한국어·한국문화 프로그램을 별도로 운영하여 국내외 학생이 함께 배우고 성장하는 <span className="font-semibold text-green-600">양방향 국제교육 환경</span>을 만들어가고 있습니다.
            </p>
          </div>
        </div>
      </div>

      {/* Philosophy Section - White Background */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="text-3xl">🌐</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              운영 철학
            </h2>
            <p className="text-gray-600">Educational Philosophy</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-2xl p-10 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-green-600 mb-6">
                "배움이 즐겁고, 세계로 이어지는 국제교육"
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                DGTC는 단순한 언어학습을 넘어, 언어를 통해 세상을 이해하고 협력할 수 있는 <span className="font-bold text-gray-900">세계시민교육(Global Citizenship Education)</span>을 지향합니다.<br />
                영어를 중심으로 한 실용·체험형 학습, 그리고 외국인 학습자를 위한 한국어·문화 교육을 병행하여 동두천이 <span className="font-semibold text-green-600">국제교육의 교류 거점</span>이 되도록 노력하고 있습니다.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Program List Section */}
      <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center max-w-4xl mx-auto mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6 leading-tight">
          교육 프로그램
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          글로벌인재센터에서 제공하는 다양한 교육 프로그램을 확인하세요
        </p>
      </div>

      <div className="mb-16 max-w-5xl mx-auto">
        <div className="bg-white rounded-xl p-8 shadow-[rgba(0,0,0,0.05)_0px_1px_8px_0px]">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <input
                type="text"
                placeholder="프로그램을 검색해보세요..."
                className="w-full px-6 py-4 bg-[#f8f8f8] rounded-xl border-0 outline-none text-[#080f28] placeholder-[#666666] text-lg font-medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              {statusOptions.map(status => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                    selectedStatus === status
                      ? 'bg-[#c2410c] text-white shadow-[rgba(0,0,0,0.05)_0px_1px_8px_0px]'
                      : 'bg-transparent text-[rgba(8,15,40,0.5)] hover:bg-[#f8f8f8] hover:text-[#080f28]'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="grid gap-8 lg:grid-cols-3 md:grid-cols-2 grid-cols-1">
          {filteredCourses.map((course) => (
            <article key={course.id} className="group flex h-full flex-col bg-white rounded-xl shadow-[rgba(0,0,0,0.05)_0px_1px_8px_0px] hover:shadow-[rgba(0,0,0,0.1)_0px_0px_15px_0px] transition-all duration-200">
              {/* Image */}
              <div className="relative h-64 md:h-56 xl:h-80 overflow-hidden rounded-t-xl">
                <img
                  src={course.courseImage || 'https://www.bgv.co.kr/sm/upload/files/kor_popup_d8c9e8ccc4733fabdb477011bd02b93c.png'}
                  alt={course.courseName}
                  className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                />
                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium shadow-[rgba(0,0,0,0.05)_0px_1px_8px_0px] ${
                    course.status === 'recruiting' ? 'bg-green-100 text-green-800' :
                    course.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                    course.status === 'completed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {course.status === 'recruiting' ? '모집중' :
                     course.status === 'ongoing' ? '진행중' :
                     course.status === 'completed' ? '종료' : '임시저장'}
                  </span>
                </div>
                {/* Full Badge */}
                {(() => {
                  const courseApplications = getApplicationsByCourse(course.id);
                  const currentApplications = courseApplications.filter(app =>
                    app.status !== 'cancelled' && app.status !== 'rejected'
                  );
                  return currentApplications.length >= course.maxStudents && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-[#c2410c] text-white">
                        마감
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col justify-between px-6 pb-6 pt-6">
                <div>
                  <h3 className="text-xl font-bold text-[#080f28] mb-3 line-clamp-2 leading-tight">
                    {course.courseName}
                  </h3>

                  <p className="text-[#666666] text-base mb-6 line-clamp-2 leading-relaxed">
                    {course.courseDescription}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-base">
                      <span className="font-medium text-[#080f28] w-20">강사</span>
                      <span className="text-[#666666]">{course.instructor}</span>
                    </div>
                    <div className="flex items-center text-base">
                      <span className="font-medium text-[#080f28] w-20">신청기간</span>
                      <span className="text-[#666666]">{formatDate(course.registrationStartDate)} ~ {formatDate(course.registrationEndDate)}</span>
                    </div>
                    <div className="flex items-center text-base">
                      <span className="font-medium text-[#080f28] w-20">수강료</span>
                      <span className="font-bold text-[#c2410c]">{formatPrice(course.tuition)}원</span>
                    </div>
                    <div className="flex items-center text-base">
                      <span className="font-medium text-[#080f28] w-20">신청현황</span>
                      <span className="text-[#666666]">
                        {(() => {
                          const courseApplications = getApplicationsByCourse(course.id);
                          const currentApplications = courseApplications.filter(app =>
                            app.status !== 'cancelled' && app.status !== 'rejected'
                          );
                          return `${currentApplications.length}/${course.maxStudents}명`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center text-base">
                      <span className="font-medium text-[#080f28] w-20">교육기간</span>
                      <span className="text-[#666666]">{formatDate(course.courseStartDate)} ~ {formatDate(course.courseEndDate)}</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-8">
                    {(() => {
                      const courseApplications = getApplicationsByCourse(course.id);
                      const currentApplications = courseApplications.filter(app =>
                        app.status !== 'cancelled' && app.status !== 'rejected'
                      );
                      const applicationRate = Math.round((currentApplications.length / course.maxStudents) * 100);
                      return (
                        <>
                          <div className="flex justify-between text-sm text-[#666666] mb-3">
                            <span>신청률</span>
                            <span className="font-medium">{applicationRate}%</span>
                          </div>
                          <div className="w-full bg-[#f2f2f2] rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                currentApplications.length >= course.maxStudents
                                  ? 'bg-[#c2410c]'
                                  : 'bg-[#1a237e]'
                              }`}
                              style={{ width: `${Math.min(applicationRate, 100)}%` }}
                            ></div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Button */}
                <Link
                  to={`/programs/${course.id}`}
                  className="inline-flex justify-center items-center rounded-full bg-[#c2410c] px-6 py-4 text-white font-medium shadow-[rgb(168,51,13)_0px_4px_0px_0px] hover:shadow-[rgb(168,51,13)_0px_2px_0px_0px] hover:translate-y-[2px] transition-all duration-150 text-lg"
                >
                  상세정보 보기
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[#666666] text-lg">검색 결과가 없습니다.</p>
        </div>
      )}
      </div>

      <Footer />
    </div>
  );
};

export default ProgramList;